/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitry Pavlov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.13.0
 */

import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { infoTranslationSelector } from '@pages/PageSubscriberId/components/Info/selectors';
import { IInfoTariffDiscount } from '@pages/PageSubscriberId/components/Info/types';
import { useSubscriberIdContext } from '@pages/PageSubscriberId/context';
import { subscriberIdRegionIdSelector } from '@pages/PageSubscriberId/selectors';

import { useGetTariffQuery } from '../store';
import { SubscriberIdInfoDiscountGroup } from './SubscriberIdInfoDiscountGroup';
import { mapRemainsToDiscounts } from './utils/mapRemainsToDiscounts';

export type TInfoTariffDiscountsOptionalValues = Omit<IInfoTariffDiscount, 'value' | 'valueTotal'> & {
    value?: IInfoTariffDiscount['value'] | string;
    valueTotal?: IInfoTariffDiscount['valueTotal'] | string;
};

const SubscriberIdInfoTariffDiscounts = () => {
    const translation = useSelector(infoTranslationSelector());

    const isChangesCalculatingRemainsAndRatePlanFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.CHANGES_CALCULATING_REMAINS_AND_RATE_PLAN),
    );

    const { subscriberId, userType } = useSubscriberIdContext();
    const regionId = useSelector(subscriberIdRegionIdSelector(subscriberId, userType));

    const { discounts, ratePlanRemains, othersRemains } = useGetTariffQuery(
        { subscriberId, regionId },
        {
            skip: !regionId,
            selectFromResult: ({ data }) => ({
                discounts: data?.discounts,
                ratePlanRemains: data?.ratePlanRemains,
                othersRemains: data?.othersRemains,
            }),
        },
    );

    const ratePlanRemainsMemoized = useMemo(() => {
        return mapRemainsToDiscounts(ratePlanRemains, translation.units);
    }, [ratePlanRemains, translation.units]);

    const otherRemainsMemoized = useMemo(() => {
        return mapRemainsToDiscounts(othersRemains, translation.units);
    }, [othersRemains, translation.units]);

    return (
        <div className="subscriber-id-info__block subscriber-id-info__tariff-discounts">
            {isChangesCalculatingRemainsAndRatePlanFeatureEnabled ? (
                <>
                    {!!ratePlanRemainsMemoized?.length && (
                        <SubscriberIdInfoDiscountGroup
                            discounts={ratePlanRemainsMemoized}
                            titleTranslation={translation.tariffDiscounts.title}
                        />
                    )}
                    {!!otherRemainsMemoized?.length && (
                        <SubscriberIdInfoDiscountGroup
                            discounts={otherRemainsMemoized}
                            titleTranslation={translation.tariffDiscounts.otherTitle}
                        />
                    )}
                </>
            ) : (
                <>
                    {!!discounts?.length && (
                        <SubscriberIdInfoDiscountGroup
                            discounts={discounts}
                            titleTranslation={translation.tariffDiscounts.title}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default SubscriberIdInfoTariffDiscounts;
